import { useEffect, useState } from "react";
import { CalendarDays, Image as ImageIcon, LayoutGrid, Lock, Megaphone, Ticket, Users2 } from "lucide-react";
import { Link, NavLink } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { api, formatApiError } from "../../lib/api";

export const activityPosts = [];

export const galleryImages = [];

export const upcomingEvents = [
  {
    title: "New Event",
    date: "Coming soon",
    time: "To be announced",
    venue: "Oswal Sabha Professional Forum, Udaipur",
    audience: "public",
    summary: "Registration is open for the upcoming OSPF event.",
    details: "Share your details now. If fees are applicable, the form will show the QR payment step.",
    registerLink: "/events/new-event/register",
  },
];

const tabs = [
  { to: "/activities/feed", label: "Feed", icon: LayoutGrid },
  { to: "/activities", label: "Activities", icon: Megaphone, end: true },
  { to: "/activities/events", label: "Events", icon: CalendarDays },
  { to: "/activities/gallery", label: "Gallery", icon: ImageIcon },
];

export default function ActivitiesShell({ children, introTitle, introSubtitle }) {
  const { member, loading: memberLoading } = useAuth();
  const isMember = Boolean(member && member !== false);
  const [stats, setStats] = useState({
    events_shared: 0,
    photo_stories: 0,
    member_updates: 0,
  });

  useEffect(() => {
    let active = true;

    const loadStats = async () => {
      try {
        const { data } = await api.get("/stats/public");
        if (!active || !data) return;
        setStats({
          events_shared: Number(data.events_shared || 0),
          photo_stories: Number(data.photo_stories || 0),
          member_updates: Number(data.member_updates || 0),
        });
      } catch (error) {
        console.error(formatApiError(error));
      }
    };

    loadStats();
    const timer = setInterval(loadStats, 60000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, []);

  return (
    <div data-testid="activities-page">
      <section className="container-px pt-12 sm:pt-16 md:pt-24 pb-10 sm:pb-12">
        <div className="max-w-4xl">
          <div className="overline mb-5">Activities</div>
          <h1 className="font-heading text-3xl sm:text-4xl md:text-6xl font-light tracking-tight text-ink leading-[1.05]">
            {introTitle}
          </h1>
          <p className="mt-5 sm:mt-7 text-base sm:text-lg text-ink-soft leading-relaxed max-w-3xl">
            {introSubtitle}
          </p>
        </div>
      </section>

      <section className="container-px pb-10 sm:pb-12">
        <div className="rounded-3xl bg-white border border-black/5 shadow-premium p-3 md:p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <NavLink
                  key={tab.label}
                  to={tab.to}
                  end={tab.end}
                  className={({ isActive }) =>
                    `rounded-2xl px-4 py-3 md:py-4 flex items-center justify-center gap-2 text-sm md:text-base font-medium transition ${
                      isActive
                        ? "bg-brand text-white shadow-md"
                        : "bg-cream-deep text-ink-soft hover:text-ink"
                    }`
                  }
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </NavLink>
              );
            })}
          </div>
        </div>
      </section>

      <section className="container-px pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8">
          <aside className="lg:col-span-4 space-y-6">
            <div className="card-premium">
              <div className="overline mb-3">Quick stats</div>
              <div className="space-y-4">
                {[
                  ["Events shared", stats.events_shared],
                  ["Photo stories", stats.photo_stories],
                  ["Member updates", stats.member_updates],
                ].map(([label, value]) => (
                  <div key={label} className="flex items-center justify-between gap-4">
                    <span className="text-ink-soft">{label}</span>
                    <span className="font-heading text-2xl text-ink">{value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="card-premium">
              <div className="overline mb-3">Want to share?</div>
              <p className="text-ink-soft leading-relaxed">
                Share your summit, get-together, or service photos with the
                admin team, and we will feature them here for everyone to enjoy.
              </p>
              <Link to="/contact" className="btn-primary mt-6 inline-flex">
                Send an update
              </Link>
            </div>

            <div className="card-premium">
              <div className="overline mb-3">Upcoming events</div>
              <div className="space-y-4">
                {upcomingEvents.length > 0 ? upcomingEvents.map((event) => {
                  const locked = event.audience === "member" && !isMember;
                  return (
                    <div
                      key={event.title}
                      className={`rounded-2xl border p-4 ${locked ? "border-dashed border-black/10 bg-cream-deep/70" : "border-black/5 bg-white"}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="inline-flex items-center gap-1.5 rounded-full bg-cream-deep px-2.5 py-1 text-[11px] font-medium text-brand-secondary">
                            {event.audience === "member" ? <Lock className="h-3 w-3" /> : <Ticket className="h-3 w-3" />}
                            {event.audience === "member" ? "Members only" : "Open to all"}
                          </div>
                          <h3 className="mt-3 font-heading text-xl font-light text-ink">
                            {event.title}
                          </h3>
                        </div>
                        <CalendarDays className="h-5 w-5 text-ink-muted shrink-0" />
                      </div>

                      <div className="mt-4 space-y-1 text-sm text-ink-soft">
                        <div>
                          {event.date} · {event.time}
                        </div>
                        <div>{event.venue}</div>
                      </div>

                      <p className="mt-3 text-sm text-ink-soft leading-relaxed">
                        {locked ? event.summary : event.details}
                      </p>

                      {locked ? (
                        <div className="mt-4 rounded-xl bg-white/80 p-3 text-sm text-ink-muted">
                          Sign in as a member to see the full event note and seat details.
                        </div>
                      ) : (
                        <Link
                          to={event.registerLink || "/activities/events"}
                          className="btn-primary mt-4 inline-flex w-full justify-center"
                        >
                          Register now
                        </Link>
                      )}
                    </div>
                  );
                }) : (
                  <div className="rounded-2xl border border-dashed border-black/10 bg-cream-deep/60 p-4 text-sm text-ink-muted">
                    No upcoming events have been posted yet.
                  </div>
                )}
              </div>
              {!memberLoading && !isMember && (
                <Link
                  to="/member/login"
                  className="btn-secondary mt-5 inline-flex w-full justify-center"
                >
                  Sign in to see member details
                </Link>
              )}
            </div>
          </aside>

          <div className="lg:col-span-8 space-y-6">{children}</div>
        </div>
      </section>
    </div>
  );
}
